import { TFunction } from 'i18next';
import _get from 'lodash/get';
import * as Yup from 'yup';
import Input from '@lib/components/Input/Input';
import Select from '@lib/components/Select/Select';
import { FieldItem } from '@lib/interfaces/form';
import {
  getDefaultNumberValidation,
  getEmailValidation,
  getFieldValidation,
  getOptionsArrayValidation,
} from '@lib/utils/validationUtils';
import { InputTypes } from '@lib/components/Input/enums';
import UpgradePlanFieldNames from '@lib/enums/fieldNames/upgradePlanFieldNames';
import {
  GetUserAccessByCompany,
  getFeatureSelectOptions,
  isUserLimitFeatureSelected,
} from '@lib/components/UpgradePlanModal/utils/upgradePlanFeatureOptions';

type FormFieldsArgs = {
  t: TFunction<'translation', undefined>;
  getUserAccessByCompany?: GetUserAccessByCompany;
  isContactUsMode?: boolean;
  usersLimit?: number | null;
};

export default function getFormFields({
  t,
  getUserAccessByCompany,
  isContactUsMode,
  usersLimit,
}: FormFieldsArgs): FieldItem[] {
  return [
    {
      name: UpgradePlanFieldNames.ContactEmail,
      label: t('your-contact-email'),
      element: Input,
      validation: getEmailValidation({ required: true, t }),
    },
    {
      name: UpgradePlanFieldNames.Features,
      label: isContactUsMode
        ? t('request-topic')
        : t('what-would-you-like-to-upgrade'),
      element: Select,
      validation: getOptionsArrayValidation({ t, required: !isContactUsMode }),
      componentProps: {
        isMulti: true,
        isSearchable: false,
        maxMenuHeight: 400,
        usePortal: true,
        options: getFeatureSelectOptions({
          t,
          getUserAccessByCompany,
          isContactUsMode,
        }),
      },
    },
    {
      name: UpgradePlanFieldNames.DesiredUserLimit,
      label: t('desired-user-limit'),
      element: Input,
      isVisibleFn: isUserLimitFeatureSelected,
      isUseWatch: true,
      componentProps: {
        type: InputTypes.number,
        min: 1,
      },
      getDescription: () =>
        t('current-user-limit-value', { limit: usersLimit ?? '-' }),
      validation: Yup.string().when(
        UpgradePlanFieldNames.Features,
        (deps, schema) =>
          isUserLimitFeatureSelected({
            [UpgradePlanFieldNames.Features]: _get(deps, [0]),
          })
            ? getDefaultNumberValidation({ t, required: true })
            : schema.nullable(),
      ),
    },
    {
      name: UpgradePlanFieldNames.Message,
      label: t('your-message-to-us'),
      element: Input,
      componentProps: {
        type: InputTypes.textarea,
      },
      validation: getFieldValidation({
        required: true,
        t,
      }),
    },
  ];
}
